<template>
  <div class="sv-list">
    <!-- 查询条件：折叠后只显示标题栏 -->
    <el-card class="search-card" shadow="never">
      <div slot="header" class="search-header">
        <span class="search-title" @click="searchExpanded = !searchExpanded">
          查询条件
          <i :class="searchExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
        </span>
      </div>
      <el-collapse-transition>
        <el-form v-show="searchExpanded" :inline="true" :model="queryForm" size="small" class="search-form" @submit.native.prevent>
          <el-form-item label="科室">
            <department-select v-model="queryForm.department" placeholder="全部科室" clearable style="width: 150px" />
          </el-form-item>
          <el-form-item label="设备名称">
            <el-input v-model="queryForm.device_name" placeholder="设备名称" clearable style="width: 170px" />
          </el-form-item>
          <el-form-item label="校验日期">
            <el-date-picker
              v-model="queryForm.verify_date"
              type="daterange"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              clearable
              style="width: 260px"
            />
          </el-form-item>
          <el-form-item label="有效期至">
            <el-date-picker
              v-model="queryForm.expire_date"
              type="daterange"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              clearable
              style="width: 260px"
            />
          </el-form-item>
          <el-form-item class="search-buttons">
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh-right" @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>
      </el-collapse-transition>
    </el-card>

    <!-- 表格 -->
    <el-card shadow="never" class="table-card">
      <div class="table-toolbar">
        <el-button type="success" icon="el-icon-plus" @click="openAdd">新增提醒</el-button>
        <el-button type="warning" icon="el-icon-download" :disabled="selectedRows.length === 0" @click="handleExportSelected">导出选中</el-button>
        <el-button
          type="primary"
          icon="el-icon-folder"
          :disabled="selectedRows.length === 0"
          @click="handleExportCerts"
        >导出资质</el-button>
      </div>
      <!-- 到期汇总：登录进来第一眼就能看到几台过期 / 即将到期 -->
      <expiry-summary
        :expired="stats.expired"
        :soon="stats.soon"
        :total="stats.total"
        unit="台"
      />

      <el-table
        ref="mainTable"
        :data="list"
        row-key="id"
        border
        class="sv-table safety-valve-list-table"
        :row-class-name="rowClassName"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="45" align="center" fixed="left" :reserve-selection="true" />
        <el-table-column type="index" :index="mainIndex" label="序号" width="55" align="center" />
        <el-table-column prop="department" label="科室" width="110" show-overflow-tooltip />
        <el-table-column prop="device_name" label="设备名称" min-width="140" show-overflow-tooltip />
        <el-table-column prop="device_model" label="设备型号" width="120" show-overflow-tooltip />
        <el-table-column prop="sn" label="SN" width="120" show-overflow-tooltip />
        <el-table-column label="安全阀类型" width="100" align="center">
          <template slot-scope="scope">{{ scope.row.valve_type_display || '-' }}</template>
        </el-table-column>
        <el-table-column prop="valve_model" label="安全阀型号" width="120" show-overflow-tooltip />
        <el-table-column prop="factory_no" label="出厂编号" width="130" show-overflow-tooltip />
        <el-table-column prop="manufacture_date" label="制造日期" width="110" align="center" />
        <el-table-column prop="verify_date" label="校验日期" width="110" align="center" />
        <el-table-column prop="verify_cycle" label="校验周期" width="90" align="center" />
        <el-table-column prop="expire_date" label="有效期至" width="110" align="center" />
        <el-table-column prop="nominal_diameter" label="公称通径" width="100" align="center" />
        <el-table-column prop="working_pressure" label="工作压力" width="100" align="center" />
        <el-table-column prop="required_set_pressure" label="要求整定压力" width="110" align="center" />
        <el-table-column prop="set_pressure" label="整定压力" width="100" align="center" />
        <el-table-column prop="working_medium" label="工作介质" width="90" align="center" />
        <el-table-column prop="seal_test_pressure" label="密封试验压力" width="110" align="center" />
        <el-table-column label="校验结果" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.verify_result === 'qualified' ? 'success' : 'danger'" size="medium">
              {{ scope.row.verify_result_display || '-' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="PDF报告" width="100" align="center">
          <template slot-scope="scope">
            <a v-if="scope.row.pdf_path" :href="mediaUrl(scope.row.pdf_path)" target="_blank" class="pdf-link">查看/下载</a>
            <span v-else class="pdf-none">未上传</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="130" show-overflow-tooltip />
        <el-table-column label="距到期" width="100" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.days_until_expire !== null"
              :type="scope.row.days_until_expire < 0 ? 'danger' : (scope.row.days_until_expire <= 30 ? 'warning' : 'info')"
              size="medium">
              <template v-if="scope.row.days_until_expire < 0">已过期</template>
              <template v-else-if="scope.row.days_until_expire <= 30">待报检</template>
              <template v-else>{{ scope.row.days_until_expire }}天</template>
            </el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240" align="center" fixed="right">
          <template slot-scope="scope">
            <el-tooltip :content="archiveTip(scope.row)" placement="top">
              <el-button type="text" icon="el-icon-time" class="archive-text" @click="openArchiveHistory(scope.row)" @mouseenter.native="onArchiveIconEnter(scope.row)" />
            </el-tooltip>
            <el-button type="text" class="archive-text" @click="openArchive(scope.row)">归档</el-button>
            <el-button type="text" @click="openEdit(scope.row)">编辑</el-button>
            <el-button type="text" class="danger-text" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pager"
        background
        layout="total, sizes, prev, pager, next"
        :total="total"
        :page-size="pageSize"
        :page-sizes="[10, 20, 50]"
        :current-page.sync="currentPage"
        @size-change="handleSizeChange"
        @current-change="fetchList"
      />
    </el-card>

    <!-- 新增 / 编辑弹窗 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="860px" :close-on-click-modal="false" @closed="resetForm">
      <el-form :model="form" :rules="rules" ref="form" label-width="120px" class="sv-form">
        <el-divider content-position="left">设备信息</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="使用科室" prop="department" :class="ocrItemClass('department')">
              <!-- 下拉选择同样触发 OCR 回填标记清除（onOcrFieldEdit 逻辑保留） -->
              <department-select v-model="form.department" placeholder="使用科室" @change="onOcrFieldEdit('department')" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="设备名称" prop="device_name" :class="ocrItemClass('device_name')">
              <el-input v-model="form.device_name" placeholder="设备名称" @input="onOcrFieldEdit('device_name')" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="设备型号" :class="ocrItemClass('device_model')">
              <el-input v-model="form.device_model" placeholder="设备型号" @input="onOcrFieldEdit('device_model')" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="SN" :class="ocrItemClass('sn')">
              <el-input v-model="form.sn" placeholder="SN" @input="onOcrFieldEdit('sn')" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider content-position="left">安全阀信息</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="安全阀类型" :class="ocrItemClass('valve_type')">
              <el-select v-model="form.valve_type" placeholder="请选择" style="width: 100%" @change="onOcrFieldEdit('valve_type')">
                <el-option label="弹簧式" value="spring" />
                <el-option label="脉冲式" value="pulse" />
                <el-option label="先导式" value="pilot" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="出厂编号" prop="factory_no" :class="ocrItemClass('factory_no')">
              <el-input v-model="form.factory_no" placeholder="出厂编号" @input="onOcrFieldEdit('factory_no')" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="安全阀型号" :class="ocrItemClass('valve_model')">
              <el-input v-model="form.valve_model" placeholder="安全阀型号" @input="onOcrFieldEdit('valve_model')" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="制造日期" :class="ocrItemClass('manufacture_date')">
              <el-date-picker v-model="form.manufacture_date" type="date" value-format="yyyy-MM-dd" placeholder="请选择制造日期" style="width: 100%" @change="onOcrFieldEdit('manufacture_date')" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="校验日期" prop="verify_date" :class="ocrItemClass('verify_date')">
              <el-date-picker v-model="form.verify_date" type="date" value-format="yyyy-MM-dd" placeholder="请选择校验日期" style="width: 100%" @change="onOcrFieldEdit('verify_date')" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="校验周期" :class="ocrItemClass('verify_cycle')">
              <el-input v-model="form.verify_cycle" placeholder="请输入月份，如 12" @input="onOcrFieldEdit('verify_cycle')">
                <template slot="suffix">月</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="有效期至">
              <el-date-picker v-model="form.expire_date" type="date" value-format="yyyy-MM-dd" placeholder="自动计算" style="width: 100%" :disabled="true" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="工作介质" :class="ocrItemClass('working_medium')">
              <el-input v-model="form.working_medium" placeholder="如 蒸汽" @input="onOcrFieldEdit('working_medium')" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="公称通径" :class="ocrItemClass('nominal_diameter')">
              <el-input v-model="form.nominal_diameter" placeholder="如 DN50" @input="onOcrFieldEdit('nominal_diameter')">
                <template slot="suffix">mm</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="工作压力" :class="ocrItemClass('working_pressure')">
              <el-input v-model="form.working_pressure" placeholder="如 1.6" @input="onOcrFieldEdit('working_pressure')">
                <template slot="suffix">MPa</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="要求整定压力" :class="ocrItemClass('required_set_pressure')">
              <el-input v-model="form.required_set_pressure" placeholder="如 1.0" @input="onOcrFieldEdit('required_set_pressure')">
                <template slot="suffix">MPa</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="整定压力" :class="ocrItemClass('set_pressure')">
              <el-input v-model="form.set_pressure" placeholder="如 1.05" @input="onOcrFieldEdit('set_pressure')">
                <template slot="suffix">MPa</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="密封试验压力" :class="ocrItemClass('seal_test_pressure')">
              <el-input v-model="form.seal_test_pressure" placeholder="如 0.9" @input="onOcrFieldEdit('seal_test_pressure')">
                <template slot="suffix">MPa</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="校验结果" :class="ocrItemClass('verify_result')">
              <el-select v-model="form.verify_result" placeholder="请选择" style="width: 100%" @change="onOcrFieldEdit('verify_result')">
                <el-option label="合格" value="qualified" />
                <el-option label="不合格" value="unqualified" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="上传报告">
          <div v-if="form.pdf_path && !pdfFile && !imageFile" class="pdf-existing">
            <a :href="mediaUrl(form.pdf_path)" target="_blank">查看当前文件</a>
          </div>
          <el-tabs v-model="uploadType" type="border-card" class="sv-upload-tabs">
            <el-tab-pane label="PDF 报告" name="pdf">
              <el-upload
                ref="upload"
                :auto-upload="false"
                :limit="1"
                accept=".pdf"
                drag
                :file-list="pdfFileList"
                :on-change="handlePdfChange"
                :on-remove="handlePdfRemove"
                class="pdf-uploader"
              >
                <i class="el-icon-upload"></i>
                <div class="el-upload__text">将安全阀检定报告 PDF 拖到此处，或<em>点击上传</em></div>
                <div slot="tip" class="el-upload__tip">优先使用文字型 PDF（可复制文字），识别率最高。</div>
              </el-upload>
            </el-tab-pane>
            <el-tab-pane label="图片拍照" name="image">
              <el-upload
                ref="imageUpload"
                :auto-upload="false"
                :limit="1"
                accept=".jpg,.jpeg,.png"
                drag
                :file-list="imageFileList"
                :on-change="handleImageChange"
                :on-remove="handleImageRemove"
                class="pdf-uploader"
              >
                <i class="el-icon-camera"></i>
                <div class="el-upload__text">将安全阀检定报告照片拖到此处，或<em>点击上传</em></div>
                <div slot="tip" class="el-upload__tip">图片将使用 RapidOCR 识别，清晰度越高效果越好。</div>
              </el-upload>
            </el-tab-pane>
          </el-tabs>
          <div class="ocr-actions">
            <el-button type="warning" icon="el-icon-magic-stick" :loading="extracting" @click="handleExtract">识别报告</el-button>
            <el-button type="success" icon="el-icon-document-checked" :loading="savingTemplate" :disabled="!ocrFilled" @click="handleSaveTemplate">存为模板识别</el-button>
            <el-tooltip content="用当前这份「已识别并核对好」的报告生成坐标模板，之后同版式报告按坐标精准回填（不影响本条保存）。" placement="top">
              <i class="el-icon-question tmpl-help"></i>
            </el-tooltip>
          </div>
          <el-alert v-if="ocrHint" :title="ocrHint" type="warning" :closable="false" show-icon class="ocr-hint-alert" />
        </el-form-item>
        <el-form-item label="备注" :class="ocrItemClass('remark')">
          <el-input v-model="form.remark" type="textarea" :rows="2" placeholder="备注" @input="onOcrFieldEdit('remark')" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-checkbox v-if="ocrFilled" v-model="ocrConfirmed" class="ocr-confirm-check">
          已核对识别结果（{{ backfilledKeys.length }} 项）无误
        </el-checkbox>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">保存</el-button>
      </span>
    </el-dialog>

    <!-- 归档（重新校验）弹窗 -->
    <el-dialog title="安全阀归档（重新校验）" :visible.sync="archiveDialogVisible" width="760px" :close-on-click-modal="false" @closed="resetArchiveForm">
      <el-alert
        type="warning"
        :closable="false"
        show-icon
        title="归档后将把旧报告移入归档文件夹（按 科室~设备名称~出厂编号~校验日期 命名，年份归档），并以新的校验日期更新本记录、存入新报告。其余内容不可修改，记录将保留在列表中。"
      />
      <el-form :model="archiveForm" :rules="archiveRules" ref="archiveForm" label-width="120px" class="sv-form archive-form">
        <el-divider content-position="left">设备信息（不可修改）</el-divider>
        <el-row :gutter="20" style="margin-top: 12px">
          <el-col :span="12">
            <el-form-item label="使用科室">
              <el-input :value="archiveForm.department" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="设备名称">
              <el-input :value="archiveForm.device_name" disabled />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="出厂编号">
              <el-input :value="archiveForm.factory_no" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="安全阀类型">
              <el-input :value="archiveForm.valve_type_display" disabled />
            </el-form-item>
          </el-col>
        </el-row>
        <el-divider content-position="left">重新校验信息（可修改）</el-divider>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="校验日期" prop="verify_date">
              <el-date-picker v-model="archiveForm.verify_date" type="date" value-format="yyyy-MM-dd" placeholder="请选择校验日期" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="校验周期">
              <el-input v-model="archiveForm.verify_cycle" placeholder="请输入月份，如 12">
                <template slot="suffix">月</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="有效期至">
              <el-date-picker v-model="archiveForm.expire_date" type="date" value-format="yyyy-MM-dd" placeholder="自动计算" style="width: 100%" :disabled="true" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="新校验报告" prop="pdf_file">
          <el-upload
            ref="archiveUpload"
            :auto-upload="false"
            :limit="1"
            accept=".pdf"
            drag
            :file-list="archivePdfFileList"
            :on-change="handleArchivePdfChange"
            :on-remove="handleArchivePdfRemove"
            class="pdf-uploader"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将新的校验报告 PDF 拖到此处，或<em>点击上传</em>（单个文件）</div>
          </el-upload>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="archiveDialogVisible = false">取消</el-button>
        <el-button type="warning" icon="el-icon-folder-checked" @click="submitArchive">确认归档</el-button>
      </span>
    </el-dialog>

    <!-- 归档记录查看弹窗 -->
    <el-dialog title="安全阀归档记录" :visible.sync="archiveHistoryVisible" width="760px" :close-on-click-modal="false">
      <el-alert
        v-if="archiveRow"
        type="info"
        :closable="false"
        show-icon
        :title="`「${archiveRow.department || '-'} / ${archiveRow.device_name}」的历史归档文件（旧报告按归档批次存放于「归档」文件夹，按 科室~设备名称~安全阀出厂编号~校验日期 命名）`"
      />
      <el-table
        v-loading="archiveHistoryLoading"
        :data="archiveFiles"
        border
        size="small"
        empty-text="暂无归档记录"
        style="margin-top: 12px"
      >
        <el-table-column type="index" label="序号" width="55" align="center" />
        <el-table-column prop="year" label="年份" width="90" align="center" />
        <el-table-column prop="name" label="归档文件名" min-width="280" show-overflow-tooltip />
        <el-table-column label="大小" width="90" align="center">
          <template slot-scope="scope">{{ formatSize(scope.row.size) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="80" align="center">
          <template slot-scope="scope">
            <a :href="scope.row.url" target="_blank" class="pdf-link">查看</a>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer">
        <el-button @click="archiveHistoryVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 导出资质：年份选择弹窗 -->
    <el-dialog title="选择导出年份" :visible.sync="exportCertVisible" width="420px" :close-on-click-modal="false">
      <div class="export-year-tip">以下年份存在已选记录的报告（含当前报告与已归档报告），请勾选需要导出的年份（默认全选）：</div>
      <el-checkbox-group v-model="exportCertYears" class="export-year-group">
        <el-checkbox v-for="y in exportCertYearOptions" :key="y" :label="y">{{ y }} 年</el-checkbox>
      </el-checkbox-group>
      <span slot="footer">
        <el-button @click="exportCertVisible = false">取消</el-button>
        <el-button type="primary" icon="el-icon-download" :loading="exporting" @click="confirmExportCerts">确认导出</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import api from '../api/safetyValve'

import DepartmentSelect from './DepartmentSelect.vue'
import ExpirySummary from './ExpirySummary.vue'

export default {
  name: 'SafetyValveList',
  components: { DepartmentSelect, ExpirySummary },
  data() {
    return {
      // 顶部汇总：已过期 / 30 天内到期 / 合计（跟随当前筛选，后端 stats 接口给）
      stats: { expired: 0, soon: 0, total: 0 },
      list: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      ordering: '',
      searchExpanded: false,
      queryForm: {
        department: '',
        device_name: '',
        verify_date: ['', ''],
        expire_date: ['', ''],
      },
      dialogVisible: false,
      dialogTitle: '新增安全阀提醒',
      editingId: null,
      pdfFile: null,
      pdfFileList: [],
      // ---- 识别报告（来自安全阀测试页的功能）----
      imageFile: null,
      imageFileList: [],
      uploadType: 'pdf',
      extracting: false,
      savingTemplate: false,
      ocrHint: '',
      ocrFilled: false,    // 是否已识别回填（控制确认复选框与「存为模板识别」可用性）
      ocrConfirmed: false, // 用户是否已勾选「确认无误」
      backfilledKeys: [],  // 本次识别回填的字段 key（黄底标记）
      _applyingOcr: false, // 程序化回填中，抑制 onOcrFieldEdit 复位确认
      selectedRows: [],
      form: this.emptyForm(),
      rules: {
        device_name: [{ required: true, message: '请输入设备名称', trigger: 'blur' }],
        factory_no: [{ required: true, message: '请输入出厂编号', trigger: 'blur' }],
        verify_date: [{ required: true, message: '请选择校验日期', trigger: 'change' }],
      },
      // 归档弹窗
      archiveDialogVisible: false,
      archiveId: null,
      archiveRow: {},
      archivePdfFile: null,
      archivePdfFileList: [],
      // 归档记录查看弹窗
      archiveHistoryVisible: false,
      archiveHistoryLoading: false,
      archiveFiles: [],
      archiveRow: null,
      archiveCounts: {},
      archiveForm: {
        department: '',
        device_name: '',
        factory_no: '',
        valve_type_display: '',
        verify_date: '',
        expire_date: '',
        verify_cycle: '',
      },
      archiveRules: {
        verify_date: [{ required: true, message: '请选择校验日期', trigger: 'change' }],
      },
      // 导出资质弹窗状态
      exportCertVisible: false,
      exportCertYears: [],
      exportCertYearOptions: [],
      exporting: false,
    }
  },
  mounted() {
    this.fetchList()
  },
  watch: {
    'form.verify_date': 'calcExpireDate',
    'form.verify_cycle': 'calcExpireDate',
    'archiveForm.verify_date': 'calcArchiveExpireDate',
    'archiveForm.verify_cycle': 'calcArchiveExpireDate',
  },
  methods: {
    emptyForm() {
      return {
        department: '',
        device_name: '',
        device_model: '',
        sn: '',
        valve_type: '',
        valve_model: '',
        factory_no: '',
        manufacture_date: '',
        verify_date: '',
        verify_cycle: '',
        expire_date: '',
        nominal_diameter: '',
        working_pressure: '',
        required_set_pressure: '',
        set_pressure: '',
        working_medium: '蒸汽',
        seal_test_pressure: '',
        verify_result: '',
        remark: '',
        pdf_path: '',
      }
    },
    mediaUrl(path) {
      if (!path) return ''
      return '/media/' + path
    },
    // 校验日期 + 校验周期(月) = 有效期至，处理月末溢出
    calcExpireDate() {
      const dateStr = this.form.verify_date
      const months = parseInt(this.form.verify_cycle, 10)
      if (!dateStr || isNaN(months) || months <= 0) {
        this.form.expire_date = ''
        return
      }
      const parts = dateStr.split('-')
      if (parts.length !== 3) return
      const y = parseInt(parts[0], 10)
      const m = parseInt(parts[1], 10)
      const d = parseInt(parts[2], 10)
      if (isNaN(y) || isNaN(m) || isNaN(d)) return
      const totalMonths = y * 12 + (m - 1) + months
      const newY = Math.floor(totalMonths / 12)
      const newM = (totalMonths % 12) + 1
      const lastDay = new Date(newY, newM, 0).getDate()
      const newD = Math.min(d, lastDay)
      this.form.expire_date = `${newY}-${String(newM).padStart(2, '0')}-${String(newD).padStart(2, '0')}`
    },
    calcArchiveExpireDate() {
      const dateStr = this.archiveForm.verify_date
      const months = parseInt(this.archiveForm.verify_cycle, 10)
      if (!dateStr || isNaN(months) || months <= 0) {
        this.archiveForm.expire_date = ''
        return
      }
      const parts = dateStr.split('-')
      if (parts.length !== 3) return
      const y = parseInt(parts[0], 10)
      const m = parseInt(parts[1], 10)
      const d = parseInt(parts[2], 10)
      if (isNaN(y) || isNaN(m) || isNaN(d)) return
      const totalMonths = y * 12 + (m - 1) + months
      const newY = Math.floor(totalMonths / 12)
      const newM = (totalMonths % 12) + 1
      const lastDay = new Date(newY, newM, 0).getDate()
      const newD = Math.min(d, lastDay)
      this.archiveForm.expire_date = `${newY}-${String(newM).padStart(2, '0')}-${String(newD).padStart(2, '0')}`
    },
    buildParams() {
      const p = { page: this.currentPage, page_size: this.pageSize }
      const q = this.queryForm
      if (q.department) p.department = q.department
      if (q.device_name) p.device_name = q.device_name
      if (q.verify_date && q.verify_date.length === 2 && q.verify_date[0] && q.verify_date[1]) {
        p.verify_date_start = q.verify_date[0]
        p.verify_date_end = q.verify_date[1]
      }
      if (q.expire_date && q.expire_date.length === 2 && q.expire_date[0] && q.expire_date[1]) {
        p.expire_date_start = q.expire_date[0]
        p.expire_date_end = q.expire_date[1]
      }
      if (this.ordering) p.ordering = this.ordering
      return p
    },
    // 顶部汇总：不带分页参数，只要筛选条件
    async fetchStats() {
      try {
        const p = this.buildParams()
        delete p.page
        delete p.page_size
        const res = await api.stats(p)
        const d = res.data || {}
        this.stats = {
          expired: d.expired || 0,
          soon: d.soon || 0,
          total: d.total || 0,
        }
      } catch (e) {
        // 汇总失败静默保持上次值，不打扰列表使用
      }
    },
    async fetchList() {
      try {
        const res = await api.list(this.buildParams())
        const data = res.data
        this.list = data.results || data
        this.total = data.count || (Array.isArray(data) ? data.length : 0)
        // 汇总与列表同源筛选，列表刷新后顺手刷新（失败不影响列表）
        this.fetchStats()
      } catch (e) {
        // 错误已由拦截器提示
      }
    },
    handleSearch() {
      this.currentPage = 1
      this.fetchList()
    },
    // 切换每页条数：回到第 1 页，防止按旧的深页码请求越界
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
      this.fetchList()
    },
    handleReset() {
      this.queryForm = {
        department: '',
        device_name: '',
        verify_date: ['', ''],
        expire_date: ['', ''],
      }
      this.ordering = ''
      this.currentPage = 1
      this.fetchList()
    },
    // 到期行高亮：已过期=红、30 天内待报检=橙
    // ⚠️ 用 row-class-name + td 样式，不要用 row-style：
    // row-style 写在 <tr> 上，会被 App.vue 暖米白主题给 td 刷的不透明底色盖掉。
    rowClassName({ row }) {
      const d = row.days_until_expire
      if (d == null) return ''
      if (d < 0) return 'expired-row'
      if (d <= 30) return 'soon-row'
      return ''
    },
    // ---- 上传处理 ----
    handlePdfChange(file, fileList) {
      this.pdfFile = file.raw
      this.pdfFileList = fileList.slice(-1)
      // PDF 与图片二选一
      this.imageFile = null
      this.imageFileList = []
      if (this.$refs.imageUpload) this.$refs.imageUpload.clearFiles()
    },
    handlePdfRemove() {
      this.pdfFile = null
      this.pdfFileList = []
    },
    handleImageChange(file, fileList) {
      this.imageFile = file.raw
      this.imageFileList = fileList.slice(-1)
      // 图片与 PDF 二选一
      this.pdfFile = null
      this.pdfFileList = []
      if (this.$refs.upload) this.$refs.upload.clearFiles()
    },
    handleImageRemove() {
      this.imageFile = null
      this.imageFileList = []
    },
    // ---- 识别报告（来自安全阀测试页）----
    ocrItemClass(key) {
      return { 'ocr-filled': this.backfilledKeys.indexOf(key) !== -1 }
    },
    onOcrFieldEdit(key) {
      if (this._applyingOcr) return
      const i = this.backfilledKeys.indexOf(key)
      if (i !== -1) {
        // 改动了回填字段，需重新核对确认
        this.backfilledKeys.splice(i, 1)
        this.ocrConfirmed = false
      }
    },
    async handleExtract() {
      if (!this.pdfFile && !this.imageFile) {
        this.$message.warning('请先上传 PDF 报告或图片')
        return
      }
      this.extracting = true
      this._applyingOcr = true
      try {
        const fd = new FormData()
        fd.append(this.pdfFile ? 'pdf_file' : 'image_file', this.pdfFile || this.imageFile)
        const res = await api.extractPdf(fd)
        const { data, hint } = res.data
        this.ocrHint = hint || ''
        // 逐项回填（识别值为空不覆盖表单现有值）；有效期至由校验日期+周期自动计算，不直接回填
        const fillKeys = [
          'department', 'device_name', 'device_model', 'sn',
          'valve_type', 'valve_model', 'factory_no',
          'manufacture_date', 'verify_date', 'verify_cycle',
          'nominal_diameter', 'working_pressure', 'required_set_pressure',
          'set_pressure', 'working_medium', 'seal_test_pressure',
          'verify_result', 'remark',
        ]
        const filled = []
        fillKeys.forEach(key => {
          const v = data[key]
          if (v !== undefined && v !== null && v !== '') {
            this.form[key] = String(v)
            filled.push(key)
          }
        })
        this.backfilledKeys = filled
        this.ocrFilled = filled.length > 0
        this.ocrConfirmed = false
        // 校验日期/周期识别出来后，触发有效期自动计算
        if (!this.form.expire_date) this.calcExpireDate()
        if (hint) {
          this.$message.warning(hint)
        } else if (filled.length) {
          this.$message.success(`已回填 ${filled.length} 项，黄色标注部分请对照原报告核对`)
        } else {
          this.$message.warning('未能识别出有效字段，请检查报告清晰度或改用文字型 PDF')
        }
      } catch (e) {
        const data = e.response && e.response.data
        const msg = (data && data.detail) || '识别失败，请检查后端服务'
        this.$message.error(msg)
      } finally {
        this.extracting = false
        this._applyingOcr = false
      }
    },
    async handleSaveTemplate() {
      if (!this.pdfFile && !this.imageFile) {
        this.$message.warning('请先上传报告并完成识别')
        return
      }
      // 建模板会把「当前表单值 + 它所在的位置」一起固化，必须先人工校对，
      // 否则错值会被后续同版式报告照抄。
      try {
        await this.$confirm(
          '将按当前表单内容建立坐标模板，请确保已人工校对识别结果——错值会被后续同版式报告照抄。' +
          '建立模板不影响本条记录的保存。',
          '存为模板识别',
          { confirmButtonText: '已校对，存为模板', cancelButtonText: '再检查', type: 'warning' }
        )
      } catch (e) {
        return
      }
      this.savingTemplate = true
      try {
        const fd = new FormData()
        fd.append(this.pdfFile ? 'pdf_file' : 'image_file', this.pdfFile || this.imageFile)
        // 把当前表单值作为校准后的字段一并提交
        const fields = [
          'department', 'device_name', 'device_model', 'sn',
          'valve_type', 'valve_model', 'factory_no',
          'manufacture_date', 'verify_date', 'verify_cycle', 'expire_date',
          'nominal_diameter', 'working_pressure', 'required_set_pressure',
          'set_pressure', 'working_medium', 'seal_test_pressure',
          'verify_result', 'remark',
        ]
        fields.forEach(f => {
          const v = this.form[f]
          if (v !== null && v !== undefined && v !== '') fd.append(f, v)
        })
        const res = await api.saveTemplate(fd)
        const anchored = res.data.anchored_fields || []
        const missing = res.data.missing_fields || []
        this.$message.success(`${res.data.detail}${anchored.length ? '：' + anchored.join('、') : ''}`)
        if (missing.length) {
          this.$message({
            type: 'warning',
            duration: 8000,
            showClose: true,
            message: `${missing.length} 个字段未建立锚点（${missing.join('、')}），识别时将走坐标/正则兜底。建议核对表单值后重新存一次模板。`,
          })
        }
      } catch (e) {
        const data = e.response && e.response.data
        const msg = (data && data.detail) || '保存模板失败，请检查后端服务'
        this.$message.error(msg)
      } finally {
        this.savingTemplate = false
      }
    },
    // ---- 新增 / 编辑 ----
    handleSelectionChange(rows) {
      this.selectedRows = rows || []
    },
    openAdd() {
      this.dialogTitle = '新增安全阀提醒'
      this.editingId = null
      this.form = this.emptyForm()
      this.pdfFile = null
      this.pdfFileList = []
      this.resetOcrState()
      this.dialogVisible = true
    },
    openEdit(row) {
      this.dialogTitle = '编辑安全阀提醒'
      this.editingId = row.id
      this.form = this.emptyForm()
      this.form.department = row.department || ''
      this.form.device_name = row.device_name || ''
      this.form.device_model = row.device_model || ''
      this.form.sn = row.sn || ''
      this.form.valve_type = row.valve_type || ''
      this.form.valve_model = row.valve_model || ''
      this.form.factory_no = row.factory_no || ''
      this.form.manufacture_date = row.manufacture_date || ''
      this.form.verify_date = row.verify_date || ''
      this.form.verify_cycle = row.verify_cycle || ''
      this.form.expire_date = row.expire_date || ''
      this.form.nominal_diameter = row.nominal_diameter || ''
      this.form.working_pressure = row.working_pressure || ''
      this.form.required_set_pressure = row.required_set_pressure || ''
      this.form.set_pressure = row.set_pressure || ''
      this.form.working_medium = row.working_medium || '蒸汽'
      this.form.seal_test_pressure = row.seal_test_pressure || ''
      this.form.verify_result = row.verify_result || ''
      this.form.remark = row.remark || ''
      this.form.pdf_path = row.pdf_path || ''
      this.pdfFile = null
      this.pdfFileList = []
      this.resetOcrState()
      this.dialogVisible = true
    },
    buildFormData() {
      const fd = new FormData()
      const fields = [
        'department', 'device_name', 'device_model', 'sn',
        'valve_type', 'valve_model', 'factory_no',
        'manufacture_date', 'verify_date', 'verify_cycle', 'expire_date',
        'nominal_diameter', 'working_pressure', 'required_set_pressure',
        'set_pressure', 'working_medium', 'seal_test_pressure',
        'verify_result', 'remark',
      ]
      fields.forEach(f => {
        const v = this.form[f]
        if (v !== null && v !== undefined && v !== '') fd.append(f, v)
      })
      if (this.pdfFile) fd.append('pdf_file', this.pdfFile)
      else if (this.imageFile) fd.append('image_file', this.imageFile)
      return fd
    },
    async submitForm() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        // OCR 回填后必须先勾选「已核对识别结果」方可保存（与计量/场所性能模块一致）
        if (this.ocrFilled && !this.ocrConfirmed) {
          this.$message.warning('请先勾选「已核对识别结果无误」再保存')
          return
        }
        try {
          const fd = this.buildFormData()
          if (this.editingId) {
            await api.update(this.editingId, fd)
            this.$message.success('保存成功')
          } else {
            await api.create(fd)
            this.$message.success('新增成功')
          }
          this.dialogVisible = false
          this.fetchList()
        } catch (e) {
          const data = e.response && e.response.data
          let msg = '保存失败，请检查后端服务'
          if (data) {
            if (data.factory_no) msg = data.factory_no[0]
            else if (data.detail) msg = data.detail
          }
          this.$message.error(msg)
        }
      })
    },
    // ---- 归档 ----
    openArchive(row) {
      this.archiveId = row.id
      this.archiveRow = row
      this.archiveForm = {
        department: row.department || '',
        device_name: row.device_name || '',
        factory_no: row.factory_no || '',
        valve_type_display: row.valve_type_display || '',
        verify_date: row.verify_date || '',
        expire_date: row.expire_date || '',
        verify_cycle: row.verify_cycle || '',
      }
      this.archivePdfFile = null
      this.archivePdfFileList = []
      this.archiveDialogVisible = true
      this.$nextTick(() => this.calcArchiveExpireDate())
    },
    handleArchivePdfChange(file, fileList) {
      this.archivePdfFile = file.raw
      this.archivePdfFileList = fileList.slice(-1)
    },
    handleArchivePdfRemove() {
      this.archivePdfFile = null
      this.archivePdfFileList = []
    },
    async submitArchive() {
      this.$refs.archiveForm.validate(async valid => {
        if (!valid) return
        // 若原记录有报告，归档必须上传新报告，避免静默丢失
        const hadPdf = !!this.archiveRow.pdf_path
        if (hadPdf && !this.archivePdfFile) {
          this.$message.warning('该记录原有校验报告，请上传新的报告后再归档')
          return
        }
        if (!this.archivePdfFile) {
          this.$message.warning('请上传一份新的校验报告文件')
          return
        }
        try {
          const fd = new FormData()
          fd.append('verify_date', this.archiveForm.verify_date)
          if (this.archiveForm.expire_date) fd.append('expire_date', this.archiveForm.expire_date)
          if (this.archiveForm.verify_cycle !== '' && this.archiveForm.verify_cycle != null) {
            fd.append('verify_cycle', this.archiveForm.verify_cycle)
          }
          if (this.archivePdfFile) fd.append('pdf_file', this.archivePdfFile)
          await api.archive(this.archiveId, fd)
          this.$message.success('归档成功')
          this.archiveDialogVisible = false
          this.$set(this.archiveCounts, this.archiveId, undefined)
          this.fetchList()
        } catch (e) {
          const data = e.response && e.response.data
          let msg = '归档失败，请检查后端服务'
          if (data) {
            if (data.detail) msg = data.detail
            else if (data.verify_date) msg = data.verify_date[0]
          }
          this.$message.error(msg)
        }
      })
    },
    resetArchiveForm() {
      this.archiveForm = {
        department: '',
        device_name: '',
        factory_no: '',
        valve_type_display: '',
        verify_date: '',
        expire_date: '',
        verify_cycle: '',
      }
      this.archivePdfFile = null
      this.archivePdfFileList = []
      if (this.$refs.archiveUpload) this.$refs.archiveUpload.clearFiles()
    },
    // 查看归档记录：拉取本条记录在硬盘「归档」文件夹中的历史报告文件
    async openArchiveHistory(row) {
      this.archiveRow = row
      this.archiveHistoryVisible = true
      this.archiveHistoryLoading = true
      this.archiveFiles = []
      try {
        const res = await api.archiveFiles(row.id)
        this.archiveFiles = (res.data && res.data.results) || []
        this.$set(this.archiveCounts, row.id, this.archiveFiles.length)
      } catch (e) {
        this.$message.error('获取归档记录失败，请检查后端服务')
      } finally {
        this.archiveHistoryLoading = false
      }
    },
    formatSize(n) {
      if (n === null || n === undefined) return '-'
      if (n < 1024) return n + ' B'
      if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB'
      return (n / 1024 / 1024).toFixed(1) + ' MB'
    },
    // 归档图标悬浮提示文案：读缓存的条数；未加载过则提示“查看归档记录”
    archiveTip(row) {
      const c = this.archiveCounts[row.id]
      if (c === undefined) return '查看归档记录'
      if (c === 0) return '暂无归档记录'
      if (c === null) return '加载中…'
      return `${c} 条归档记录`
    },
    // 首次悬浮图标时懒加载该记录的归档条数（之后缓存，避免重复请求）
    onArchiveIconEnter(row) {
      if (this.archiveCounts[row.id] !== undefined) return
      this.$set(this.archiveCounts, row.id, null)
      api.archiveFiles(row.id).then(res => {
        const cnt = (res.data && res.data.count) != null ? res.data.count : 0
        this.$set(this.archiveCounts, row.id, cnt)
      }).catch(() => {
        this.$set(this.archiveCounts, row.id, 0)
      })
    },
    // ---- 删除 ----
    async handleDelete(row) {
      try {
        await this.$confirm(
          `确认删除「${row.device_name}」的安全阀记录吗？删除后其报告文件将移入「删除」文件夹，原文件夹将清理，数据库记录保留但不再展示。`,
          '删除确认',
          { type: 'warning' }
        )
      } catch (e) {
        return
      }
      try {
        await api.delete(row.id)
        this.$message.success('删除成功')
        this.fetchList()
      } catch (e) {
        this.$message.error('删除失败，请检查后端服务')
      }
    },
    // ---- 导出 ----
    // 主列表序号：跨页连续编号（第 2 页从 11 开始，不重新起 1）
    mainIndex(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1
    },
    // 拉取与当前筛选/排序一致的全量列表，建立 id→序号 映射（序号=在全量结果中的位置，与页面显示一致）
    // 后端 max_page_size=100，超过 100 条时循环翻页补齐
    async fetchSeqMap() {
      const map = {}
      try {
        const pageSize = 100
        let page = 1
        let total = Infinity
        while ((page - 1) * pageSize < total) {
          const params = { ...this.buildParams(), page, page_size: pageSize }
          const res = await api.list(params)
          const data = res.data
          const rows = data.results || data
          rows.forEach((row, i) => { map[row.id] = (page - 1) * pageSize + i + 1 })
          total = data.count || rows.length
          if (!rows.length) break
          page++
        }
      } catch (e) {
        // 拉取失败返回空映射，序号留空但不阻塞导出
      }
      return map
    },
    async exportRows(rows, fileNamePrefix) {
      if (!rows || !rows.length) {
        this.$message.warning('没有可导出的记录')
        return
      }
      const seqMap = await this.fetchSeqMap()
      const headers = {
        seq: '序号',
        department: '使用科室',
        device_name: '设备名称',
        device_model: '设备型号',
        sn: 'SN',
        valve_type_display: '安全阀类型',
        valve_model: '安全阀型号',
        factory_no: '出厂编号',
        manufacture_date: '制造日期',
        verify_date: '校验日期',
        verify_cycle: '校验周期',
        expire_date: '有效期至',
        nominal_diameter: '公称直径',
        working_pressure: '工作压力',
        required_set_pressure: '要求整定压力',
        set_pressure: '整定压力',
        working_medium: '工作介质',
        seal_test_pressure: '密封试验压力',
        verify_result_display: '校验结果',
        remark: '备注',
        days_until_expire: '距到期天数',
        created_at: '创建时间',
        updated_at: '更新时间',
      }
      const exportData = rows.map(row => {
        const item = {}
        Object.keys(headers).forEach(key => {
          let value = row[key]
          if (key === 'seq') value = seqMap[row.id] || ''
          item[headers[key]] = value !== undefined && value !== null ? value : ''
        })
        return item
      })
      const ws = XLSX.utils.json_to_sheet(exportData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '安全阀提醒')
      XLSX.writeFile(wb, `${fileNamePrefix}_${this.formatDateForFile(new Date())}.xlsx`)
      this.$message.success(`已成功导出 ${rows.length} 条记录`)
    },
    handleExportSelected() {
      // 仅导出当前表格复选框选中的记录
      if (!this.selectedRows || this.selectedRows.length === 0) {
        this.$message.warning('请先勾选要导出的记录')
        return
      }
      this.exportRows(this.selectedRows, '安全阀提醒_选中')
    },
    // 导出资质：向后端查询所选记录可导出报告的年份（含当前报告 + 已归档报告），再弹出年份多选框
    async handleExportCerts() {
      if (!this.selectedRows.length) {
        this.$message.warning('请先在列表中至少选择一条需要导出的记录')
        return
      }
      const ids = this.selectedRows.map(r => r.id)
      try {
        const res = await api.availableYears(ids)
        const years = (res.data && res.data.years) || []
        if (!years.length) {
          this.$message.warning('所选记录均没有可导出（当前或已归档）的报告，暂无法确定导出年份')
          return
        }
        this.exportCertYearOptions = years
        this.exportCertYears = [...years]
        this.exportCertVisible = true
      } catch (e) {
        const msg = e.response?.data?.detail || '获取可导出年份失败，请检查后端服务'
        this.$message.error(msg)
      }
    },
    async confirmExportCerts() {
      if (!this.exportCertYears.length) {
        this.$message.warning('请至少选择一个导出年份')
        return
      }
      this.exporting = true
      try {
        const ids = this.selectedRows.map(r => r.id)
        const res = await api.exportFiles({ ids, years: this.exportCertYears })
        this.saveBlob(res.data, `安全阀报告_导出_${this.formatDateForFile(new Date())}.zip`)
        this.$message.success(`已导出 ${this.exportCertYears.join('/')} 年报告`)
        this.exportCertVisible = false
      } catch (e) {
        let msg = '导出失败，请检查后端服务'
        const resp = e.response
        if (resp && (resp.status === 400 || resp.status === 403)) {
          try {
            const text = await resp.data.text()
            const j = JSON.parse(text)
            if (j.detail) msg = j.detail
          } catch (_) {}
        } else if (resp && resp.status === 401) {
          msg = '未登录或登录已过期，请重新登录'
        }
        this.$message.error(msg)
      } finally {
        this.exporting = false
      }
    },
    saveBlob(buffer, fileName) {
      const blob = (buffer && buffer instanceof Blob) ? buffer : new Blob([buffer])
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    },
    formatDateForFile(d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const h = String(d.getHours()).padStart(2, '0')
      const min = String(d.getMinutes()).padStart(2, '0')
      const s = String(d.getSeconds()).padStart(2, '0')
      return `${y}${m}${day}_${h}${min}${s}`
    },
    // ---- 识别状态重置 ----
    resetOcrState() {
      this.imageFile = null
      this.imageFileList = []
      this.uploadType = 'pdf'
      this.extracting = false
      this.savingTemplate = false
      this.ocrHint = ''
      this.ocrFilled = false
      this.ocrConfirmed = false
      this.backfilledKeys = []
      this._applyingOcr = false
      this.$nextTick(() => {
        this.$refs.upload && this.$refs.upload.clearFiles()
        this.$refs.imageUpload && this.$refs.imageUpload.clearFiles()
      })
    },
    resetForm() {
      this.form = this.emptyForm()
      this.pdfFile = null
      this.pdfFileList = []
      this.resetOcrState()
      if (this.$refs.upload) this.$refs.upload.clearFiles()
    },
  },
}
</script>

<style scoped>
.sv-list {
  padding: 16px;
}
.search-card /deep/ .el-card__body {
  padding: 0;
}
.search-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.search-title {
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  user-select: none;
}
.search-title:hover {
  color: #409eff;
}
/* 查询区缩小一号：label 13px，间距收紧（控件尺寸由 size="small" 接管） */
.search-form /deep/ .el-form-item__label {
  font-size: 13px;
}
.search-form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 10px 12px;
}
.search-form .el-form-item {
  margin-bottom: 8px;
}
.search-buttons {
  margin-left: auto;
}
.table-card {
  margin-top: 16px;
}
.table-toolbar {
  margin-bottom: 12px;
  text-align: left;
}
/* 导出资质年份选择弹窗 */
.export-year-tip {
  color: #606266;
  font-size: 14px;
  margin-bottom: 14px;
  line-height: 1.6;
}
.export-year-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-left: 6px;
}
.export-year-group .el-checkbox {
  margin-right: 0;
}
.sv-table /deep/ .el-table__body tr:hover > td {
  background-color: #ecf5ff !important;
}
/* 到期高亮（与报告提醒/计量提醒统一配色，能在暖米白底上分辨） */
.sv-table /deep/ .el-table__body tr.expired-row > td {
  background-color: #fad7d5 !important;
}
.sv-table /deep/ .el-table__body tr.expired-row > td:first-child {
  border-left: 3px solid #f56c6c;
}
.sv-table /deep/ .el-table__body tr.soon-row > td {
  background-color: #fbe9c9 !important;
}
.sv-table /deep/ .el-table__body tr.soon-row > td:first-child {
  border-left: 3px solid #e6a23c;
}
.sv-table /deep/ .el-table__body tr.expired-row:hover > td {
  background-color: #f6c6c3 !important;
}
.sv-table /deep/ .el-table__body tr.soon-row:hover > td {
  background-color: #f7dfb2 !important;
}
.sv-table /deep/ .el-table__body tr > td {
  transition: background-color 0.2s;
}
.pager {
  margin-top: 16px;
  text-align: right;
}
.pdf-link {
  color: #409eff;
  margin: 0 2px;
}
.pdf-none {
  color: #c0c4cc;
}
.archive-text {
  color: #e6a23c;
}
.danger-text {
  color: #f56c6c;
}
.sv-form /deep/ .el-input__inner {
  border-color: #409eff;
  font-size: 15px;
}
.sv-form /deep/ .el-textarea__inner {
  border-color: #409eff;
  font-size: 15px;
}
.sv-form /deep/ .el-divider__text {
  font-weight: bold;
  color: #409eff;
}
.pdf-uploader {
  width: 100%;
}
/* ---- 识别报告（来自安全阀测试页）---- */
.sv-upload-tabs {
  width: 100%;
}
.ocr-actions {
  margin-top: 10px;
}
.ocr-hint-alert {
  margin-top: 10px;
}
.tmpl-help {
  margin-left: 8px;
  color: #909399;
  cursor: help;
  font-size: 16px;
  vertical-align: middle;
}
.ocr-confirm-check {
  margin-right: 10px;
}
/* OCR 回填字段黄底标记 */
.sv-form /deep/ .ocr-filled .el-input__inner,
.sv-form /deep/ .ocr-filled .el-textarea__inner {
  background: #fdf6ec;
}
.pdf-existing {
  margin-bottom: 8px;
}
.pdf-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}
</style>

<style>
/* 安全阀列表页：表头水平显示 + 表头与所有单元格水平居中（全局样式 + 唯一类名隔离，不影响其他表格） */
.safety-valve-list-table.el-table th > .cell {
  white-space: nowrap !important;
  writing-mode: horizontal-tb !important;
  transform: none !important;
  text-align: center !important;
}
.safety-valve-list-table.el-table th > .cell > .el-table__column-filter-trigger {
  writing-mode: horizontal-tb !important;
  transform: none !important;
}
.safety-valve-list-table.el-table td > .cell {
  text-align: center !important;
}
</style>
